import React, { useEffect, useRef, useState } from "react";
import { api, brl } from "../lib/api";
import { toast } from "sonner";
import { MagnifyingGlass, Barcode, Camera, SpeakerHigh, SpeakerSlash } from "@phosphor-icons/react";
import BarcodeScanner from "./BarcodeScanner";
import { beepSuccess, beepError, primeAudio } from "../lib/beep";

const SOUND_KEY = "pdv_scan_sound";

// Search + USB/Bluetooth (Enter=barcode) + câmera contínua. onPick(product) deve retornar a quantidade atual do item.
export function ProductPicker({ onPick, testidPrefix = "picker" }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [sound, setSound] = useState(() => {
    if (typeof window === "undefined") return true;
    return localStorage.getItem(SOUND_KEY) !== "0";
  });
  const [scanFeedback, setScanFeedback] = useState(null);
  const boxRef = useRef(null);
  const lastLookupRef = useRef({ code: "", ts: 0 });
  const fbTimerRef = useRef(null);

  useEffect(() => {
    if (!q || q.length < 2) { setResults([]); setOpen(false); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/products`, { params: { q, status: "active" } });
        setResults(data || []);
        setOpen(true);
      } catch { setResults([]); }
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const toggleSound = () => {
    primeAudio();
    setSound((s) => { const n = !s; localStorage.setItem(SOUND_KEY, n ? "1" : "0"); if (n) beepSuccess(); return n; });
  };

  const flashFeedback = (fb) => {
    setScanFeedback(fb);
    if (fbTimerRef.current) clearTimeout(fbTimerRef.current);
    fbTimerRef.current = setTimeout(() => setScanFeedback(null), 2500);
  };

  const addAndFeedback = (prod) => {
    const qty = onPick(prod);
    if (sound) beepSuccess();
    const shownQty = typeof qty === "number" ? qty : undefined;
    flashFeedback({ status: "ok", name: prod.nome_produto, qty: shownQty ?? 1, code: prod.codigo_barras });
    toast.success(`${prod.nome_produto} adicionado${shownQty ? ` · qtd ${shownQty}` : ""}`);
  };

  const lookupBarcode = async (code) => {
    const c = String(code || "").replace(/\D/g, "");
    if (!c) return;
    const now = Date.now();
    // Dedupe de jitter (USB/Bluetooth pode disparar 2x): ignora o mesmo código em janela muito curta.
    if (c === lastLookupRef.current.code && now - lastLookupRef.current.ts < 600) return;
    lastLookupRef.current = { code: c, ts: now };
    try {
      const { data } = await api.get(`/products/barcode/${c}`);
      addAndFeedback(data);
      setQ(""); setResults([]); setOpen(false);
    } catch {
      if (sound) beepError();
      flashFeedback({ status: "error", code: c });
      toast.error(`Código ${c} não encontrado no catálogo`);
    }
  };

  const pick = (p) => {
    addAndFeedback(p);
    setQ(""); setResults([]); setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const raw = q.trim();
      if (/^\d{6,}$/.test(raw)) lookupBarcode(raw);
      else if (results.length === 1) pick(results[0]);
    }
  };

  return (
    <div className="relative mb-5" ref={boxRef} data-testid={`${testidPrefix}-container`}>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#9A8A98]" />
          <input
            className="input-underline w-full pl-11"
            placeholder="Buscar por código, nome ou bipar código de barras"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            onFocus={primeAudio}
            data-testid={`${testidPrefix}-search-input`}
          />
        </div>
        <button
          type="button"
          onClick={toggleSound}
          className={`flex min-h-[48px] items-center gap-1 rounded-xl border border-border bg-white px-3 text-xs uppercase tracking-widest transition-colors hover:bg-muted ${sound ? "text-emerald-600" : "text-brand-pink"}`}
          title={sound ? "Som ligado" : "Som desligado"}
          data-testid={`${testidPrefix}-sound-btn`}
        >
          {sound ? <SpeakerHigh size={18} /> : <SpeakerSlash size={18} />}
        </button>
        <button
          type="button"
          onClick={() => { primeAudio(); setScanFeedback(null); setScanOpen(true); }}
          className="flex min-h-[48px] items-center gap-1.5 rounded-xl border border-border bg-white px-3 text-xs uppercase tracking-widest text-brand-dark transition-colors hover:bg-muted"
          data-testid={`${testidPrefix}-camera-btn`}
        >
          <Camera size={18} /> <span className="hidden sm:inline">Câmera</span>
        </button>
      </div>

      {/* Feedback inline (para leitura USB e busca manual) */}
      {scanFeedback && !scanOpen && (
        scanFeedback.status === "ok" ? (
          <div className="mt-2 rounded-xl border border-emerald-200 bg-emerald-50 p-2.5 text-xs text-emerald-700" data-testid={`${testidPrefix}-feedback-ok`}>
            ✓ <strong>{scanFeedback.name}</strong> adicionado · qtd <strong>{scanFeedback.qty}</strong>
          </div>
        ) : (
          <div className="mt-2 rounded-xl border border-red-200 bg-red-50 p-2.5 text-xs text-red-700" data-testid={`${testidPrefix}-feedback-error`}>
            ⚠ Código <strong>{scanFeedback.code}</strong> não encontrado.
          </div>
        )
      )}

      {open && results.length > 0 && (
        <div className="absolute left-0 right-0 z-40 mt-2 max-h-64 overflow-y-auto rounded-xl border border-border bg-white shadow-pop" data-testid={`${testidPrefix}-results`}>
          {results.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => pick(p)}
              className="flex w-full items-center justify-between border-b border-border px-3.5 py-2.5 text-left transition-colors last:border-0 hover:bg-muted"
              data-testid={`${testidPrefix}-result-${p.id}`}
            >
              <div>
                <div className="text-sm font-medium text-brand-dark">{p.nome_produto}</div>
                <div className="mt-0.5 flex items-center gap-2 text-[11px] text-[#7A6A78]">
                  <span className="font-medium text-brand">{p.codigo_interno}</span>
                  {p.codigo_barras ? <span className="flex items-center gap-1"><Barcode size={12} />{p.codigo_barras}</span> : <span>sem código</span>}
                  {p.categoria ? <span>· {p.categoria}</span> : null}
                  <span>· estoque {p.quantidade_estoque}</span>
                </div>
              </div>
              <div className="tabular text-sm font-semibold text-brand-dark">{brl(p.valor_venda)}</div>
            </button>
          ))}
        </div>
      )}
      {open && results.length === 0 && q.length >= 2 && (
        <div className="absolute left-0 right-0 z-40 mt-2 rounded-xl border border-border bg-white px-3.5 py-2.5 text-xs text-[#7A6A78] shadow-pop" data-testid={`${testidPrefix}-empty`}>
          Nenhum produto encontrado.
        </div>
      )}

      <BarcodeScanner
        open={scanOpen}
        feedback={scanFeedback}
        onClose={() => setScanOpen(false)}
        onDetected={(code) => lookupBarcode(code)}
      />
    </div>
  );
}

export default ProductPicker;
